import {
  Route,
  SetSupportChatMessageReadStatus,
  SupportChatMessageResponse,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { request } from "../api"

async function fetchMessages(
  storeId: string,
): Promise<SupportChatMessageResponse[]> {
  return request({
    url: populateRoute(Route.SUPPORT_CHAT__FIND_MESSAGES, storeId),
    method: "GET",
  })
}

async function fetchUnreadCount(storeId: string): Promise<number> {
  return request({
    url: populateRoute(Route.SUPPORT_CHAT__UNREAD_COUNT_FOR_STORE, storeId),
    method: "GET",
  })
}

async function sendMessageAsStore(
  storeId: string,
  data: { content: string; attachedFiles?: File[] },
): Promise<void> {
  const formData = new FormData()
  formData.append("content", data.content)
  if (data.attachedFiles) {
    for (const file of data.attachedFiles) {
      formData.append("attachedFiles", file)
    }
  }
  return request({
    url: populateRoute(Route.SUPPORT_CHAT__SEND_AS_STORE, storeId),
    method: "POST",
    data: formData,
  })
}

async function markAsReadForStore(storeId: string): Promise<void> {
  return request({
    url: populateRoute(Route.SUPPORT_CHAT__MARK_READ_AS_STORE, storeId),
    method: "PATCH",
  })
}

async function setMessageReadStatusAsStore(
  storeId: string,
  data: SetSupportChatMessageReadStatus,
): Promise<void> {
  return request({
    url: populateRoute(
      Route.SUPPORT_CHAT__SET_MESSAGE_READ_STATUS_AS_STORE,
      storeId,
    ),
    method: "PATCH",
    data,
  })
}

const supportChatQueries = {
  all: () => ["support-chat"],
  messages: (storeId: string) =>
    queryOptions({
      queryKey: [...supportChatQueries.all(), "messages", storeId],
      queryFn: () => fetchMessages(storeId),
    }),
  unreadCount: (storeId: string) =>
    queryOptions({
      queryKey: [...supportChatQueries.all(), "unread-count", storeId],
      queryFn: () => fetchUnreadCount(storeId),
      staleTime: 5000,
    }),
}

export const useSendSupportMessage = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { content: string; attachedFiles?: File[] }) =>
      sendMessageAsStore(storeId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(supportChatQueries.messages(storeId))
    },
  })
}

export const useMarkSupportChatAsRead = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => markAsReadForStore(storeId),
    onSuccess: () => {
      queryClient.invalidateQueries(supportChatQueries.messages(storeId))
      queryClient.invalidateQueries(supportChatQueries.unreadCount(storeId))
    },
  })
}

export const useSetSupportChatMessageReadStatus = (storeId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SetSupportChatMessageReadStatus) =>
      setMessageReadStatusAsStore(storeId, data),
    onSettled: () => {
      queryClient.invalidateQueries(supportChatQueries.messages(storeId))
      queryClient.invalidateQueries(supportChatQueries.unreadCount(storeId))
    },
  })
}

export default supportChatQueries
